import type { LucideIcon } from "lucide-react"
import {
  Activity,
  Bookmark,
  Box,
  Clapperboard,
  Film,
  ImageIcon,
  LayoutDashboard,
  LibraryBig,
  MapPin,
  Package,
  Palette,
  Settings,
  Sparkles,
  SquareKanban,
  SquareStack,
  UserCog,
  Users,
  Wallet,
  Workflow,
} from "lucide-react"
import type { ProjectPermissions } from "@/types/workspace"

/** Project id from `/projects/:id/workspace…` URL (props can briefly go missing on partial reloads). */
export function readProjectIdFromWorkspaceUrl(href?: string): number | undefined {
  const source = href ?? (typeof window !== "undefined" ? window.location.pathname : "")
  const match = source.match(/\/projects\/(\d+)\/workspace(?:\/|$|\?)/)
  if (!match) return undefined
  const n = Number(match[1])
  return Number.isFinite(n) && n > 0 ? n : undefined
}

export type WorkspaceNavItem = {
  href: string
  /** i18n key in the `layout` namespace, e.g. `nav.items.overview`. */
  labelKey: string
  icon: LucideIcon
  /** Показывать только при выполнении условия */
  visible?: (ctx: { canSeeGenerations: boolean }) => boolean
}

export type WorkspaceNavGroup = {
  id: string
  /** i18n key in the `layout` namespace, e.g. `nav.groups.workspace`. */
  labelKey: string
  items: readonly WorkspaceNavItem[]
}

/** Группы пунктов бокового меню проекта (порядок как в продукте). */
export const WORKSPACE_NAV_GROUPS: readonly WorkspaceNavGroup[] = [
  {
    id: "workspace",
    labelKey: "nav.groups.workspace",
    items: [
      { href: "overview", labelKey: "nav.items.overview", icon: LayoutDashboard },
      { href: "tasks", labelKey: "nav.items.tasks", icon: SquareKanban },
      { href: "script", labelKey: "nav.items.script", icon: Clapperboard },
    ],
  },
  {
    id: "assets",
    labelKey: "nav.groups.assets",
    items: [
      { href: "characters", labelKey: "nav.items.characters", icon: Users },
      { href: "locations", labelKey: "nav.items.locations", icon: MapPin },
      { href: "props", labelKey: "nav.items.props", icon: Package },
      { href: "scenes_3d", labelKey: "nav.items.scenes_3d", icon: Box },
    ],
  },
  {
    id: "production",
    labelKey: "nav.groups.production",
    items: [
      { href: "canvas", labelKey: "nav.items.canvas", icon: Palette },
      { href: "storyboard", labelKey: "nav.items.storyboard", icon: ImageIcon },
      { href: "flow", labelKey: "nav.items.flow", icon: Workflow },
      { href: "presets", labelKey: "nav.items.presets", icon: Bookmark },
      { href: "animatics", labelKey: "nav.items.animatics", icon: Film },
      { href: "studio", labelKey: "nav.items.studioAssembly", icon: SquareStack },
    ],
  },
  {
    id: "settings",
    labelKey: "nav.groups.settings",
    items: [
      { href: "settings", labelKey: "nav.items.settings", icon: Settings },
      { href: "team", labelKey: "nav.items.team", icon: UserCog },
      { href: "billing", labelKey: "nav.items.billing", icon: Wallet },
      { href: "skills", labelKey: "nav.items.generation_skills", icon: Sparkles },
      { href: "library", labelKey: "nav.items.library", icon: LibraryBig },
      { href: "seasons", labelKey: "nav.items.seasons", icon: Film },
      { href: "generations", labelKey: "shell.monitoring.generations", icon: Activity },
    ],
  },
] as const

type NavPermissions = Pick<ProjectPermissions, "can_edit_settings" | "can_view_team" | "can_view_billing">

export function filterWorkspaceNavItemsByPermissions<T extends WorkspaceNavGroup>(
  groups: readonly T[],
  permissions: NavPermissions | null | undefined,
  canSeeGenerations: boolean,
): T[] {
  const canEditSettings = permissions?.can_edit_settings ?? true
  const canViewTeam = permissions?.can_view_team ?? false
  const canViewBilling = permissions?.can_view_billing ?? false
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.visible && !item.visible({ canSeeGenerations })) return false
        if (item.href === "settings" || item.href === "skills") return canEditSettings
        if (item.href === "team") return canViewTeam
        if (item.href === "billing") return canViewBilling
        return true
      }),
    }))
    .filter((group) => group.items.length > 0)
}
